// src/services/notifications/index.js
// Local notification delivery, permission management, vibration,
// and emergency override logic.
// FCM token management and topic subscriptions live in push.js.

// ── Permission ────────────────────────────────────────────────────
export const PERM = {
  GRANTED:     'granted',
  DENIED:      'denied',
  PENDING:     'default',
  UNSUPPORTED: 'unsupported',
};

export function getPermissionStatus() {
  if (!('Notification' in window)) return PERM.UNSUPPORTED;
  return Notification.permission; // 'granted' | 'denied' | 'default'
}

export async function requestPermission() {
  if (!('Notification' in window)) return PERM.UNSUPPORTED;
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

// ── Local notifications ───────────────────────────────────────────
// Used as fallback when the app is in the foreground and the SW
// hasn't fired, or for in-app confirmation of actions.
export function sendLocal(title, body, opts = {}) {
  if (Notification.permission !== 'granted') return null;

  const n = new Notification(title, {
    body,
    icon:               '/icon-192.png',
    badge:              '/icon-192.png',
    tag:                opts.tag ?? 'na',
    requireInteraction: opts.requireInteraction ?? false,
    silent:             opts.silent ?? false,
    data:               opts.data ?? {},
    ...opts,
  });

  n.onclick = () => {
    window.focus();
    if (opts.data?.url) window.location.href = opts.data.url;
    n.close();
  };

  return n;
}

// ── Emergency override ────────────────────────────────────────────
// Called for severity=extreme or type=evacuation.
// Fires vibration + forces visible notification + DOM event for in-app modal.
export function deliverEmergencyAlert({ title, body, alertId, url = '/news' }) {
  vibrateEmergency();

  sendLocal(title, body, {
    tag:                'na-emergency',
    requireInteraction: true,
    silent:             false,
    data:               { alertId, url },
  });

  window.dispatchEvent(new CustomEvent('na-emergency-alert', {
    detail: { title, body, alertId, alertUrl: url },
  }));
}

// ── Vibration ─────────────────────────────────────────────────────
export function vibrate(pattern = [200, 100, 200]) {
  navigator.vibrate?.(pattern);
}

export function vibrateEmergency() {
  // 3 short · 3 long · 3 short — SOS pattern
  navigator.vibrate?.([200, 100, 200, 100, 200, 200, 500, 100, 500, 100, 500, 200, 200, 100, 200, 100, 200]);
}

export function vibrateAlert() {
  navigator.vibrate?.([200, 100, 200, 100, 400]);
}

export function vibrateStop() {
  navigator.vibrate?.(0);
}

// ── Badge API ─────────────────────────────────────────────────────
export async function setBadge(count) {
  if (navigator.setAppBadge) {
    if (count > 0) await navigator.setAppBadge(count).catch(() => {});
    else           await navigator.clearAppBadge().catch(() => {});
  }
}

export async function clearBadge() {
  navigator.clearAppBadge?.().catch(() => {});
}

// ── Share ─────────────────────────────────────────────────────────
import { APP } from '@config';

export async function shareApp() {
  const payload = {
    title: APP.name,
    text:  'Free Louisiana emergency network — alerts, gas, shelters, evac routes. Works offline.',
    url:   APP.url,
  };
  if (navigator.share) return navigator.share(payload);
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(payload.url);
    return 'copied';
  }
  return 'unsupported';
}
